
import React, { useState, useEffect } from 'react';
import { useToast } from '../../providers/ToastProvider';
import { Session } from '../../peer-colab-model/Session';
import { TrainerManagment } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
// No longer need UpdateTrainer*Input or Button imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TextField from '@mui/material/TextField';
import ImageUploadComponent from '../../components/ImageUploadComponent';
import PublishStatusComponent from '../../components/PublishStatusComponent';
import { onRevert } from '../../utils/GeneralHelpers';
import { OperationRequest } from '@peercolab/engine';
import { Trainer } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Trainers/PathItems';
import AISuggestions from '../../components/AISuggestions';
import { Published } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Core/PathItems';

interface EditTrainerProps {
  id: string;
  onBack: () => void;
}

const EditTrainer: React.FC<EditTrainerProps> = ({ id, onBack }) => {
  const { onError } = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [thumbnail, setThumbnail] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [signature, setSignature] = useState<any>(null);
  const [experience, setExperience] = useState('');
  const [trainer, setTrainer] = useState<Trainer | undefined>(undefined);
  const [state, setState] = useState<Published>({ state: 'draft', isDirty: false });
  // No longer need loading state

  // Fetch trainer details on mount and after patch
  const fetchTrainer = async () => {
    const result = await Session.getTrainerClient(id).request(TrainerManagment.getTrainer());
    result.maybeOk((trainer) => {
      setName(trainer.name ?? '');
      setDescription(trainer.description ?? '');
      setExperience(trainer.experienceInYears?.toString() ?? '');
      setThumbnail(trainer.thumbnail ?? null);
      setSignature(trainer.signature ?? null);
      setTrainer(trainer);
      setState(trainer.state);
    });
  };

  useEffect(() => {
    fetchTrainer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!trainer) return null;

  const patchField = async <R,V>(operation: OperationRequest<unknown, R>, newValue: V, currentValue: V, revertHandler: (value: V) => void): Promise<void> => {
    if (newValue === currentValue) return
    onError(onRevert((await Session.getTrainerClient(id).request(operation)), () => revertHandler(currentValue)))
      .maybeOk(fetchTrainer);
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={onBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">Edit Trainer</Typography>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <AISuggestions 
            client={Session.getTrainerClient(id)}
            currentSnapshot={JSON.stringify(trainer)}
            usageInstructions={TrainerManagment.toAgentInstructions()}
            developerMessage='The user is in the context of a single trainer that he/she is editing'
            onOperationsApplied={() => fetchTrainer()}
          />
        </Box>
        <PublishStatusComponent
          status={state}
          onTransition={async (action) => {
            return (await Session.getTrainerClient(id).request(TrainerManagment.updateTrainerState({ stateTransition: action })))
              .maybeOk(() => fetchTrainer());
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
        {/* Left column */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Trainer Name"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            onBlur={() => patchField(TrainerManagment.updateTrainerName({ name }), name, trainer.name, setName)}
            fullWidth
            autoFocus
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
            onBlur={() => patchField(TrainerManagment.updateTrainerDescription({ description }), description, trainer.description, setDescription)}
            fullWidth
            multiline
            minRows={4}
          />
        </Box>
        {/* Right column */}
        <Box sx={{ minWidth: 500, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <ImageUploadComponent value={trainer.thumbnail?.url ?? ''} onChange={async (file) => {
            onError((await Session.getTrainerClient(id).request(TrainerManagment.updateTrainerThumbnail({image: file ? {
              filename: file.name,
              mimeType: file.mimeType,
              size: file.size,
              width: file.width,
              height: file.height,
              base64Content: file.base64Content,
            } : undefined})))
              .maybeOk(() => fetchTrainer()));
          }} />
          <ImageUploadComponent label='Signature' value={trainer.signature?.url ?? ''} onChange={async (file) => {
            onError((await Session.getTrainerClient(id).request(TrainerManagment.updateTrainerSignature({image: file ? {
              filename: file.name,
              mimeType: file.mimeType,
              size: file.size,
              width: file.width,
              height: file.height,
              base64Content: file.base64Content,
            } : undefined})))
              .maybeOk(() => fetchTrainer()));
          }} />
          <TextField
            label="Experience in Years"
            type="number"
            value={experience}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExperience(e.target.value.replace(/[^\d]/g, ''))}
            onBlur={() => patchField(TrainerManagment.updateTrainerExperienceInYears({ experienceInYears: parseInt(experience, 10) }), experience, trainer.experienceInYears.toString(), setExperience)}
            fullWidth
            inputProps={{ min: 0 }}
          />
        </Box>
      </Box>
      {/* No Save button, fields update individually */}
    </Box>
  );
};

export default EditTrainer;
